import {
  CreditScore,
  Download,
  Groups,
  RocketLaunch,
  Security,
} from '@mui/icons-material';
import Skeleton from 'react-loading-skeleton';
import Fade from 'react-reveal/Fade';
import PartnersSection from '../components/PartnersSection';
import FAQSection from '../components/FAQSection';
import { useSite } from '../context/SiteContext';
import { imageUrl } from '../services/axios';
import '../styles/about.scss';
import wlAbout from '../assets/images/wl_about.jpg';

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const ADVANTAGE_ICONS = [RocketLaunch, CreditScore, Security, Groups];

const FALLBACK_CONTENT = [
  {
    tag: 'Our Story',
    title: 'Banking built for the everyday Nigerian',
    content:
      'WayaBank is part of WayaLinks, a Nigerian-built technology ecosystem connecting people, businesses, and communities to the digital economy. From informal traders in Lagos markets to SMEs across the country, we are banking the unbanked, one account at a time.',
    // ↓ Shedrack Salami — Mile 12 International Market, Ikorodu, Lagos (Unsplash, free licence)
    image: 'https://media.istockphoto.com/id/1536943029/photo/woman-preparing-to-make-contactless-payment-for-flowers.jpg?s=612x612&w=0&k=20&c=gyOjDvxlyKrOK0MKcI7ZF0ljs8fh-3luBjbiFa2wuKg=',
    isHtml: false,
  },
  {
    tag: 'Built for Growth',
    title: 'From the market stall to the boardroom',
    content:
      'Whether you sell tomatoes or run a growing business, WayaBank works for you. We have built tools for bulk transfers, invoicing, POS payments, and business loans — all from one app. One ecosystem, one settlement, zero friction.',
    // ↓ your original Medium URL — kept exactly as provided
    image: 'https://miro.medium.com/1*tipl8OaB5fFdJIPY1Ti0xA.jpeg',
    isHtml: false,
  },
];
const FALLBACK_IMG =
  'https://thumbs.dreamstime.com/b/young-african-woman-selling-tomatoes-local-market-smiling-giving-thumbs-up-gesture-167358193.jpg'

/* ─────────────────────────────────────────
   ABOUT PAGE
───────────────────────────────────────── */
function About() {
  const {
    aboutIntro,
    aboutContent,
    advantages,
    settings,
    ctaSections,
    loading,
  } = useSite();

  const getFullImageUrl = (url) => {
    if (!url) return FALLBACK_IMG;
    if (
      url.startsWith('http') ||
      url.startsWith('blob:') ||
      url.startsWith('data:')
    )
      return url;
    return `${imageUrl}/${url}`;
  };

  /* Always use FALLBACK_CONTENT images; CMS text/tag/title used if available */
  const contentRows =
    Array.isArray(aboutContent) && aboutContent.length > 0
      ? aboutContent.map((item, index) => ({
          tag: item.tag,
          title: item.title,
          content: item.content,
          image: FALLBACK_CONTENT[index]?.image || getFullImageUrl(item.image_url),
          isHtml: true,
        }))
      : FALLBACK_CONTENT;

  return (
    <div className="about-page">
      {/* Navbar Spacer / Background */}
      <div className="navbar-spacer" style={{ height: '74px', background: '#fff' }}></div>

      {/* ── 1. HERO ── */}
      <section className="about-hero" style={{ 
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.85)), url(${wlAbout})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        minHeight: '400px',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div className="about-container">
          <Fade bottom>
            <span className="about-pill">
              {loading ? <Skeleton width={80} /> : aboutIntro?.pill || 'About Us'}
            </span>

            {loading ? (
              <Skeleton width="80%" height={48} className="mb-3" />
            ) : (
              <h1
                className="about-hero-heading"
                dangerouslySetInnerHTML={{
                  __html:
                    aboutIntro?.title ||
                    'Banking is Just the Beginning.<br class="d-none d-lg-block" /> We Are the Engine of Your Growth.',
                }}
              />
            )}

            <p className="about-hero-body">
              {loading ? <Skeleton count={3} /> : aboutIntro?.content || ''}
            </p>
          </Fade>
        </div>
      </section>

      {/* ── 2. CONTENT ROWS ── */}
      <section className="about-rows-section">
        <div className="about-container">
          {loading
            ? [1, 2].map((i) => (
                <div key={i} className={`about-row ${i % 2 === 0 ? 'about-row--reverse' : ''}`}>
                  <div className="about-row-text">
                    <Skeleton height={18} width="40%" className="mb-3" />
                    <Skeleton height={36} width="75%" className="mb-3" />
                    <Skeleton count={3} height={14} />
                  </div>
                  <div className="about-row-media">
                    <Skeleton height={360} borderRadius={24} />
                  </div>
                </div>
              ))
            : contentRows.map((item, index) => (
                <div
                  key={index}
                  className={`about-row ${index % 2 !== 0 ? 'about-row--reverse' : ''}`}
                  style={{
                    borderTop: index === 0 ? 'none' : '1px solid #f2f4f7',
                  }}
                >
                  {/* Text */}
                  <div className="about-row-text">
                    <Fade left={index % 2 === 0} right={index % 2 !== 0}>
                      <div>
                        {item.tag && (
                          <span className="about-tag">{item.tag}</span>
                        )}
                        <h2 className="about-row-heading">{item.title}</h2>
                        {item.isHtml ? (
                          <div
                            className="about-row-body"
                            dangerouslySetInnerHTML={{ __html: item.content }}
                          />
                        ) : (
                          <p className="about-row-body">{item.content}</p>
                        )}
                      </div>
                    </Fade>
                  </div>

                  {/* Image */}
                  <div className="about-row-media">
                    <Fade right={index % 2 === 0} left={index % 2 !== 0}>
                      <div className="about-row-img-shell">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="about-row-img"
                          onError={(e) => {
                            e.target.src = FALLBACK_IMG;
                          }}
                        />
                      </div>
                    </Fade>
                  </div>
                </div>
              ))}
        </div>
      </section>

      {/* ── 3. MISSION ── */}
      <section className="about-mission-section">
        <div className="about-container">
          <Fade bottom>
            <div className="about-mission-card">
              <h6 className="about-mission-label">
                {loading ? (
                  <Skeleton width={120} />
                ) : (
                  aboutIntro?.mission_title || 'Our Mission'
                )}
              </h6>
              <p className="about-mission-text">
                {loading ? (
                  <Skeleton count={2} />
                ) : (
                  settings?.mission || ''
                )}
              </p>
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 4. ADVANTAGES ── */}
      <section className="about-adv-section">
        <div className="about-container">
          <Fade bottom>
            <h2 className="about-adv-heading">
              {loading ? (
                <Skeleton width="60%" />
              ) : (
                aboutIntro?.advantages_title ||
                `The ${settings?.site_name || 'WayaBank'} Advantage: Why We Stand Out`
              )}
            </h2>
          </Fade>

          <div className="about-adv-grid">
            {loading
              ? [1, 2, 3, 4].map((i) => (
                  <div key={i} className="about-adv-card">
                    <Skeleton circle height={48} width={48} className="mb-3" />
                    <Skeleton width="70%" height={18} className="mb-2" />
                    <Skeleton count={2} height={13} />
                  </div>
                ))
              : (advantages || []).map((adv, i) => {
                  const Icon = ADVANTAGE_ICONS[i % 4];
                  return (
                    <Fade bottom delay={i * 100} key={i}>
                      <div className="about-adv-card">
                        <div className="about-adv-icon">
                          <Icon style={{ color: 'var(--waya-primary)', fontSize: '22px' }} />
                        </div>
                        <h6 className="about-adv-title">{adv.title}</h6>
                        <p className="about-adv-desc">{adv.desc}</p>
                      </div>
                    </Fade>
                  );
                })}
          </div>
        </div>
      </section>

      {/* ── 5. CTA BANNER ── */}
      <section className="about-cta-section">
        <div className="about-container">
          <Fade bottom>
            <div className="about-cta-card">
              <h2 className="about-cta-heading">
                {loading ? (
                  <Skeleton width="50%" />
                ) : (
                  ctaSections?.about?.title || 'Join the Financial Revolution'
                )}
              </h2>

              <p className="about-cta-body">
                {loading ? (
                  <Skeleton count={2} />
                ) : (
                  ctaSections?.about?.subline || ''
                )}
              </p>

              <button
                className="about-cta-btn"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent('waya-open-download-popup')
                  )
                }
              >
                <Download style={{ fontSize: '18px' }} />
                Download App To Get Started
              </button>
            </div>
          </Fade>
        </div>
      </section>

      {/* ── 6. FAQ ── */}
      <section className="about-faq-section">
        <FAQSection />
      </section>

      {/* ── 7. PARTNERS ── */}
      <section className="about-partners-section">
        <PartnersSection />
      </section>

      {/* ── STYLES ── */}
      <style>{`
        .about-page {
          overflow-x: hidden;
          background: #fff;
        }

        .about-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 5%;
        }

        /* ── 1. Hero ── */
        .about-hero {
          padding: 140px 0 110px;
          text-align: center;
          color: white;
        }

        .about-pill {
          display: inline-block;
          padding: 5px 16px;
          border-radius: 999px;
          background: #fff4ec;
          border: 1px solid #ffd9be;
          color: #ff4400;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        .about-hero-heading {
          font-size: clamp(2.2rem, 5vw, 3.8rem);
          font-weight: 900;
          color: #ffffff;
          line-height: 1.1;
          letter-spacing: -0.04em;
          margin: 0 auto 24px;
          max-width: 900px;
          text-shadow: 0 2px 10px rgba(0,0,0,0.3);
        }

        .about-hero-body {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.75;
          max-width: 680px;
          margin: 0 auto;
          font-weight: 500;
        }

        /* ── 2. Content Rows ── */
        .about-rows-section {
          padding: 80px 0;
        }

        .about-row {
          display: flex;
          align-items: center;
          gap: 72px;
          padding: 72px 0;
        }

        .about-row--reverse {
          flex-direction: row-reverse;
        }

        .about-row-text,
        .about-row-media {
          flex: 1;
        }

        .about-tag {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 999px;
          background: #fff4ec;
          color: #ff4400;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 16px;
        }

        .about-row-heading {
          font-size: clamp(1.6rem, 2.8vw, 2.2rem);
          font-weight: 900;
          color: #0f172a;
          line-height: 1.15;
          letter-spacing: -0.03em;
          margin: 0 0 18px;
        }

        .about-row-body {
          font-size: 16.5px;
          color: #64748b;
          line-height: 1.8;
          margin: 0;
        }

        .about-row-img-shell {
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.10);
          aspect-ratio: 4/3;
        }

        .about-row-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }

        .about-row-img-shell:hover .about-row-img {
          transform: scale(1.03);
        }

        /* ── 3. Mission ── */
        .about-mission-section {
          padding: 60px 0;
          background: #f9fafb;
        }

        .about-mission-card {
          // background: linear-gradient(135deg, #ff4400 0%, #ff9a00 100%);
          background-color:#131b2e;
          border-radius: 28px;
          padding: 60px 52px;
          text-align: center;
          box-shadow: 0 24px 60px rgba(255, 68, 0, 0.22);
        }

        .about-mission-label {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.75);
          margin: 0 0 16px;
        }

        .about-mission-text {
          font-size: 18px;
          font-weight: 500;
          color: #fff;
          line-height: 1.75;
          max-width: 700px;
          margin: 0 auto;
        }

        /* ── 4. Advantages ── */
        .about-adv-section {
          padding: 90px 0;
          background: #fff;
        }

        .about-adv-heading {
          font-size: clamp(1.6rem, 2.8vw, 2.1rem);
          font-weight: 900;
          color: #0f172a;
          text-align: center;
          letter-spacing: -0.03em;
          margin: 0 0 52px;
        }

        .about-adv-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
        }

        .about-adv-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 20px;
          padding: 32px 26px;
          transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.3s ease;
        }

        .about-adv-card:hover {
          border-color: #ff4400;
          box-shadow: 0 8px 32px rgba(255, 68, 0, 0.10);
          transform: translateY(-4px);
        }

        .about-adv-icon {
          width: 46px;
          height: 46px;
          border-radius: 13px;
          background: rgba(255, 68, 0, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }

        .about-adv-title {
          font-size: 15px;
          font-weight: 800;
          color: #0f172a;
          margin: 0 0 10px;
          line-height: 1.3;
        }

        .about-adv-desc {
          font-size: 13.5px;
          color: #64748b;
          line-height: 1.65;
          margin: 0;
        }

        /* ── 5. CTA ── */
        .about-cta-section {
          padding: 60px 0 80px;
          background: #f9fafb;
        }

        .about-cta-card {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 28px;
          padding: 72px 52px;
          text-align: center;
          box-shadow: 0 24px 60px rgba(0,0,0,0.14);
        }

        .about-cta-heading {
          font-size: clamp(1.8rem, 3vw, 2.5rem);
          font-weight: 900;
          color: #fff;
          letter-spacing: -0.04em;
          margin: 0 0 16px;
        }

        .about-cta-body {
          font-size: 16px;
          color: rgba(255,255,255,0.65);
          line-height: 1.7;
          max-width: 520px;
          margin: 0 auto 36px;
        }

        .about-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #ff4400;
          color: #fff;
          border: none;
          padding: 14px 28px;
          border-radius: 13px;
          font-size: 14px;
          font-weight: 800;
          letter-spacing: 0.02em;
          cursor: pointer;
          box-shadow: 0 10px 28px rgba(255, 68, 0, 0.35);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }

        .about-cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 16px 36px rgba(255, 68, 0, 0.45);
        }

        /* ── 6 & 7. FAQ + Partners bg reset ── */
        .about-faq-section,
        .about-partners-section {
          background: #fff;
        }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .about-adv-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 860px) {
          .about-row,
          .about-row--reverse {
            flex-direction: column !important;
            gap: 40px;
            padding: 52px 0;
          }

          .about-mission-card,
          .about-cta-card {
            padding: 48px 24px;
            border-radius: 20px;
          }
          .about-mission-text {
            font-size: 16px;
          }
          .about-hero {
            padding: 100px 0 80px;
          }
          .about-row {
            padding: 40px 0;
          }
          .about-row-img-shell {
            border-radius: 16px;
          }
        }

        @media (max-width: 600px) {
          .about-hero {
            padding: 90px 0 64px;
          }

          .about-adv-grid {
            grid-template-columns: 1fr;
          }

          .about-adv-card {
            padding: 26px 20px;
          }
        }
      `}</style>
    </div>
  );
}

export default About;
